import { useState, useEffect, useCallback } from 'react';
import { getOneLineDrawings } from 'api/PreEForm';
import { log } from '../utils/Viewer';

// ─────────────────────────────────────────────────────────────
// useOneLineDrawings
//
// Encapsulates all fetch state for OneLine Drawings so the
// parent component only manages modal open/close logic.
//
// Parameters:
//   projectId — project.id
//   bpid      — project.bpid
//
// Returns:
//   drawings  — fetched drawing array
//   loading   — boolean fetch-in-progress flag
//   error     — error message string or null
//   refetch   — imperative refetch (call after save)
// ─────────────────────────────────────────────────────────────
const useOneLineDrawings = (projectId, bpid) => {
  const [drawings, setDrawings] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  const fetchDrawings = useCallback(async () => {
    if (!projectId) return;

    setLoading(true);
    setError(null);

    try {
      const result = await getOneLineDrawings(projectId, bpid);
      setDrawings(result ?? []);
      log('[useOneLineDrawings] Fetched drawings:', result);

      if (result?.length > 0) {
        log(
          '[useOneLineDrawings] Available URNs:',
          result.map((d) => ({ name: d.attachmentName, urn: d.documentId })),
        );
      }
    } catch (err) {
      console.error('[useOneLineDrawings] Failed to fetch drawings:', err);
      setError('Failed to load drawings');
      setDrawings([]);
    } finally {
      setLoading(false);
    }
  }, [projectId, bpid]);

  useEffect(() => {
    fetchDrawings();
  }, [fetchDrawings]);

  return { drawings, loading, error, refetch: fetchDrawings };
};

export default useOneLineDrawings;